import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent/DialogContent'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { KnockStatusHistory } from './KnockStatusHistory'
import {
  HistoryItem,
  useDeleteHistory,
  useHandlers,
  useHistoryData,
} from './hooks'
import { EntityLocationDTO } from '@user/store/map/typings'
import { MessageDialogPopup } from '@sales-center/components'
import Alert from '@mui/material/Alert'
import { useCheckPermissions } from '@user/store/authorization/permissions'

type Props = {
  open: boolean
  onClose: () => void
  onRefresh?: () => void
  onDeleteSuccess: () => void
  address?: EntityLocationDTO | null
  onUpdateAddressDetails?: (item: HistoryItem) => void
}

export function KnockStatusHistoryModal({
  open,
  onClose,
  onRefresh,
  onDeleteSuccess,
  address = null,
  onUpdateAddressDetails,
}: Props) {
  const {
    historyList,
    isLoading,
    reload,
    hasHistory,
    activeKnockStatusName,
    activeKnockStatusLocationId,
  } = useHistoryData(address)

  const { onUpdate } = useHandlers({
    onClose: () => onClose(),
    reload,
    hasExistingHistory: hasHistory,
    currentKnockStatusName: activeKnockStatusName,
    onRefresh,
    address,
  })

  const {
    deleteHistoryConfirmOpen,
    isDeletingHistory,
    openDeleteHistoryConfirm,
    closeDeleteHistoryConfirm,
    handleDeleteHistory,
  } = useDeleteHistory({
    address,
    activeKnockStatusLocationId,
    onRefresh,
    onDeleteSuccess,
  })

  const [canDeleteKnockStatusHistory] = useCheckPermissions(
    'other_delete_knock_status_history',
  )

  const theme = useTheme()
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const isSmallHeightMobile = useMediaQuery(
    '(max-height: 500px) and (pointer: coarse)',
  )
  const isFullScreen = isSmallWidth || isSmallHeightMobile

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={
        isFullScreen
          ? undefined
          : {
              sx: {
                width: {
                  xs: '95vw',
                  sm: '501px',
                },
                maxWidth: {
                  xs: '95vw',
                  sm: '501px',
                },
                maxHeight: '90vh',
                overflowY: 'auto',
              },
            }
      }
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 0,
          height: '72px',
          pl: '24px',
          pr: '15px',
          pt: '24px',
          pb: '16px',
          borderBottom: 'none',
        }}
      >
        <Typography
          variant={'h6'}
          component="span"
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
        >
          Knock Status History
        </Typography>

        <IconButton
          onClick={onClose}
          size={'medium'}
          sx={{
            color: (theme) => theme.palette.action.active,
            cursor: 'pointer',
            borderRadius: '100px',
          }}
          data-testid="action-modal-close-btn"
          data-test-id="knockStatusHistoryModalCloseBtn"
        >
          <CloseIcon fontSize={'medium'} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          px: 2.4,
          pt: 2.4,
          pb: 0.4,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <KnockStatusHistory
          historyList={historyList}
          isLoading={isLoading}
          onUpdate={onUpdate}
          onUpdateAddressDetails={onUpdateAddressDetails}
        />
      </DialogContent>

      <DialogActions
        sx={{
          p: 0,
          px: 2,
          py: 1.2,
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        {hasHistory && canDeleteKnockStatusHistory && (
          <Button
            variant="text"
            onClick={openDeleteHistoryConfirm}
            sx={{
              color: '#1565C0',
              fontWeight: 400,
              fontSize: '15px',
              letterSpacing: '0.15px',
            }}
          >
            Delete knock status history
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: '#002d5c',
            color: 'white',
            fontSize: '15px',
            '&:hover': {
              backgroundColor: '#001f40',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      {deleteHistoryConfirmOpen && (
        <MessageDialogPopup
          open={deleteHistoryConfirmOpen}
          title="Delete Knock Status History"
          message={
            <Alert severity="warning">
              This action cannot be undone. It will permanently delete all knock
              statuses and reasons.
            </Alert>
          }
          cancelClick={closeDeleteHistoryConfirm}
          cancelButtonText="Cancel"
          cancelButtonVariant="text"
          actionButtons={[
            <Button
              key="delete-knock-history-btn"
              variant="contained"
              color="primary"
              onClick={handleDeleteHistory}
              disabled={isDeletingHistory}
            >
              Delete
            </Button>,
          ]}
          actionButtonsPlacement="end"
        />
      )}
    </Dialog>
  )
}
